import { BASE_ERROR_CODES } from 'better-auth'
import {
  EMAIL_OTP_ERROR_CODES,
  emailOTPClient,
  inferAdditionalFields,
  PHONE_NUMBER_ERROR_CODES,
  phoneNumberClient,
} from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

import { env } from '@/customer/lib/env'

/**
 * Shared better-auth client for the customer portal. Used directly
 * by sign-in, OTP verification, identifier update, and session
 * lookup call sites (`emailOtp.*`, `phoneNumber.*`, `signIn.*`,
 * `useSession`, `signOut`).
 */
export const authClient = createAuthClient({
  // `VITE_API_URL` already includes the `/api` prefix; append the
  // portal-auth base path that better-auth is mounted at server-side.
  baseURL: `${env.VITE_API_URL}/portal/auth`,
  sessionOptions: {
    refetchOnWindowFocus: false,
  },
  plugins: [
    emailOTPClient(),
    phoneNumberClient(),
    // Mirror the server-side `session.additionalFields` so `useSession()`
    // typing knows about `session.impersonatedBy`. Kept as a config-based
    // schema (rather than `inferAdditionalFields<typeof auth>()`) so the
    // customer package doesn't have to import backend types.
    inferAdditionalFields({
      session: { impersonatedBy: { type: 'string', required: false } },
    }),
  ],
})

/**
 * Composed error-code map for better-auth core plus the plugins we
 * register. Each entry is `{ code, message }`; callers use it to
 * narrow error shapes (`isAuthError`) and to fall back to
 * better-auth's own copy when a code isn't overridden in UI
 * (`AuthErrorBanner`).
 *
 * `authClient.$ERROR_CODES` looks like it should give us the same
 * thing, but it's a TypeScript-only augmentation - at runtime the
 * client's Proxy returns another Proxy for that path, so `in` checks
 * and property lookups don't behave as the types suggest. Importing
 * the raw constants directly avoids that trap.
 */
export const authErrorCodes = {
  ...BASE_ERROR_CODES,
  ...EMAIL_OTP_ERROR_CODES,
  ...PHONE_NUMBER_ERROR_CODES,
} as const

export type AuthErrorCode = keyof typeof authErrorCodes

/**
 * Type guard for better-auth's client-side error shape (the `error`
 * half of `{ data, error }`). Better-auth's type annotation claims
 * `BetterFetchError`, but on the non-throw path the runtime value is
 * a plain object with the response body spread onto it -
 * `instanceof BetterFetchError` returns false. We match on the
 * presence of a `code` string that better-auth actually knows about,
 * so an arbitrary `{ code: 'whatever' }` doesn't sneak through.
 */
export function isAuthError(err: unknown): err is { code: AuthErrorCode; message?: string } {
  return (
    err !== null &&
    typeof err === 'object' &&
    'code' in err &&
    typeof err.code === 'string' &&
    err.code in authErrorCodes
  )
}
